import { useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { X, MapPin, Lock, Users, Globe, Star, Calendar, ChevronLeft } from 'lucide-react'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { supabase, BUCKETS, uploadFile } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { useQueryClient } from '@tanstack/react-query'
import type { Entry } from '@/types/database'

const ENTRY_TYPES: { value: Entry['entry_type']; label: string; icon: string }[] = [
  { value: 'journal',   label: 'Journal',   icon: '📖' },
  { value: 'photo',     label: 'Photo',     icon: '📷' },
  { value: 'video',     label: 'Video',     icon: '🎥' },
  { value: 'milestone', label: 'Milestone', icon: '🏆' },
  { value: 'voice',     label: 'Voice',     icon: '🎙' },
  { value: 'location',  label: 'Location',  icon: '📍' },
]

const PRIVACY_OPTS = [
  { value: 'private' as const, icon: Lock,  label: 'Private' },
  { value: 'friends' as const, icon: Users, label: 'Friends' },
  { value: 'public'  as const, icon: Globe, label: 'Public'  },
]

const TAGS = ['Birthday','Travel','Milestone','Family','Work','Health','Nature','Food','Celebration']

export default function EditEntryPage() {
  const navigate   = useNavigate()
  const location   = useLocation()
  const { user }   = useAuthStore()
  const qc         = useQueryClient()

  // Entry is passed via router state
  const entry: Entry = location.state?.entry

  const [title,        setTitle]        = useState(entry?.title ?? '')
  const [body,         setBody]         = useState(entry?.body ?? '')
  const [entryType,    setEntryType]    = useState<Entry['entry_type']>(entry?.entry_type ?? 'journal')
  const [privacy,      setPrivacy]      = useState<Entry['privacy']>(entry?.privacy ?? 'private')
  const [tags,         setTags]         = useState<string[]>(entry?.tags ?? [])
  const [isMilestone,  setIsMilestone]  = useState(entry?.is_milestone ?? false)
  const [locationName, setLocationName] = useState(entry?.location_name ?? '')
  const [recordedAt,   setRecordedAt]   = useState(
    entry ? format(new Date(entry.recorded_at), "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm")
  )
  const [existingUrls, setExistingUrls] = useState<string[]>(entry?.media_urls ?? [])
  const [newFiles,     setNewFiles]     = useState<File[]>([])
  const [newPreviews,  setNewPreviews]  = useState<string[]>([])
  const [saving,       setSaving]       = useState(false)
  const [error,        setError]        = useState('')

  // If no entry passed in state, go back
  if (!entry) {
    navigate(-1)
    return null
  }

  const onDrop = useCallback((accepted: File[]) => {
    setNewFiles(prev => [...prev, ...accepted])
    accepted.forEach(f => setNewPreviews(prev => [...prev, URL.createObjectURL(f)]))
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'video/*': [] },
    maxSize: 50 * 1024 * 1024,
  })

  const removeExisting = (url: string) => {
    setExistingUrls(prev => prev.filter(u => u !== url))
  }

  const removeNew = (i: number) => {
    URL.revokeObjectURL(newPreviews[i])
    setNewFiles(prev => prev.filter((_, idx) => idx !== i))
    setNewPreviews(prev => prev.filter((_, idx) => idx !== i))
  }

  const toggleTag = (tag: string) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
  }

  const handleSave = async () => {
    if (!title.trim()) { setError('Please add a title.'); return }
    setSaving(true)
    setError('')
    try {
      // Upload any new files
      let uploadedUrls: string[] = []
      if (newFiles.length) {
        uploadedUrls = await Promise.all(
          newFiles.map(f => uploadFile(BUCKETS.MEMORIES, `${user!.id}/${Date.now()}-${f.name}`, f))
        )
      }

      const { error: updateError } = await supabase
        .from('entries')
        .update({
          title:         title.trim(),
          body:          body.trim() || null,
          entry_type:    entryType,
          privacy,
          tags,
          media_urls:    [...existingUrls, ...uploadedUrls],
          location_name: locationName.trim() || null,
          is_milestone:  isMilestone,
          recorded_at:   new Date(recordedAt).toISOString(),
          updated_at:    new Date().toISOString(),
        })
        .eq('id', entry.id)

      if (updateError) throw updateError

      // Invalidate so timeline refreshes
      qc.invalidateQueries({ queryKey: ['entries', user?.id] })
      navigate(-1)
    } catch (err: any) {
      setError(err.message ?? 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div
      className="min-h-dvh bg-bg flex flex-col safe-top"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="glass sticky top-0 z-20 px-5 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-white/50"
        >
          <ChevronLeft size={20} />
          <span className="text-sm">Cancel</span>
        </button>
        <h2 className="font-serif text-lg font-bold text-white">Edit Memory</h2>
        <button
          onClick={handleSave}
          disabled={saving || !title}
          className="text-blue-light font-semibold text-sm disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="flex flex-col gap-5 px-5 py-5 pb-24">

        {/* Entry type */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {ENTRY_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setEntryType(t.value)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-sm font-medium transition-all
                ${entryType === t.value
                  ? 'border-blue bg-blue/10 text-blue-light'
                  : 'border-white/[0.07] bg-white/5 text-white/50'}`}
            >
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        {/* Title */}
        <input
          type="text"
          placeholder="Title…"
          value={title}
          onChange={e => setTitle(e.target.value)}
          className="input-field text-base font-semibold"
          autoFocus
        />

        {/* Body */}
        <textarea
          placeholder="Tell the story…"
          value={body}
          onChange={e => setBody(e.target.value)}
          className="input-field min-h-[120px] resize-none"
          rows={5}
        />

        {/* Existing media */}
        {existingUrls.length > 0 && (
          <div>
            <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">
              Current Photos
            </p>
            <div className="flex gap-2 flex-wrap">
              {existingUrls.map(url => (
                <div key={url} className="relative w-20 h-20 rounded-xl overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeExisting(url)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center"
                  >
                    <X size={10} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add new media */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all
            ${isDragActive ? 'border-blue bg-blue/5' : 'border-white/10 bg-white/[0.02]'}`}
        >
          <input {...getInputProps()} />
          <p className="text-white/30 text-sm">
            {isDragActive ? 'Drop here…' : '📷 Add more photos or videos'}
          </p>
        </div>

        {/* New media previews */}
        {newPreviews.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {newPreviews.map((url, i) => (
              <div key={url} className="relative w-20 h-20 rounded-xl overflow-hidden">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeNew(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center"
                >
                  <X size={10} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Date */}
        <div className="flex items-center gap-3">
          <Calendar size={16} className="text-white/30 flex-shrink-0" />
          <input
            type="datetime-local"
            value={recordedAt}
            onChange={e => setRecordedAt(e.target.value)}
            className="input-field flex-1 text-sm"
          />
        </div>

        {/* Location */}
        <div className="relative">
          <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Add location (optional)"
            value={locationName}
            onChange={e => setLocationName(e.target.value)}
            className="input-field pl-10"
          />
        </div>

        {/* Tags */}
        <div>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Tags</p>
          <div className="flex flex-wrap gap-2">
            {TAGS.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                  ${tags.includes(tag)
                    ? 'border-blue bg-blue/10 text-blue-light'
                    : 'border-white/[0.07] text-white/40'}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Milestone toggle */}
        <div className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/[0.07]">
          <div className="flex items-center gap-3">
            <Star size={18} className={isMilestone ? 'text-yellow-400' : 'text-white/30'} />
            <div>
              <p className="text-sm font-semibold text-white">Mark as Milestone</p>
              <p className="text-xs text-white/30">Highlighted on chronological view</p>
            </div>
          </div>
          <button
            onClick={() => setIsMilestone(!isMilestone)}
            className={`w-12 h-6 rounded-full transition-all relative ${isMilestone ? 'bg-yellow-500' : 'bg-white/10'}`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${isMilestone ? 'left-7' : 'left-1'}`} />
          </button>
        </div>

        {/* Privacy */}
        <div>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Privacy</p>
          <div className="flex gap-2">
            {PRIVACY_OPTS.map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setPrivacy(value)}
                className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs transition-all
                  ${privacy === value
                    ? 'border-blue bg-blue/10 text-blue-light'
                    : 'border-white/[0.07] text-white/40'}`}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-rose text-sm text-center bg-rose/10 border border-rose/20 rounded-xl px-4 py-2">
            {error}
          </p>
        )}

      </div>
    </motion.div>
  )
}
